import { Component, Inject, OnInit } from '@angular/core';
import {
  MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA,
  MatLegacyDialog as MatDialog,
  MatLegacyDialogRef as MatDialogRef,
} from '@angular/material/legacy-dialog';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { Observable } from 'rxjs';
import { UserInfoService } from 'src/app/shared/services/user-info.service';
import { ReportApiService } from 'src/app/shared/services/customer-api/report-api.service';
import { ReportFeedbackService } from 'src/app/shared/services/report-feedback.service';
import {
  FREQUENCY_OPTIONS,
  FrequencyOption,
  ReportType,
  Schedule,
  SubscriptionPayload,
} from 'src/app/shared/utils/reports';
import { isStatusAvailable } from 'src/app/shared/services/report-state.registry';
import { AlertsSetupComponent } from 'src/app/project/settings/alerts-setup/alerts-setup.component';
import * as moment from 'moment';

export type ReportKind = 'score' | 'due_diligence';

export interface ReportScoreTypeOption {
  value: ReportType;
  label: string;
}

export interface ReportDialogData {
  reportType: ReportType;
  titleLine1: string;
  titleLine2: string;
  status: string | null;
  entityId?: string;
  entityType?: string;
  portfolioName?: string;
  portfolioUid?: string;
  portfolioContent?: string[];
  messageLine?: string;
  searchQuery?: string;
  queryName?: string;
  reportKind?: ReportKind;
  reportLabel?: string;
  bullets?: string[];
  scoreTypeOptions?: ReportScoreTypeOption[];
}

type ReportMode = 'get_now' | 'recurring';

const PREVIEW_URLS: Record<
  'entity' | 'portfolio',
  Partial<Record<ReportType, string>>
> = {
  entity: {
    credit_watch:
      'https://reporting-service.api.yukkalab.com/content/credit_watch_tesla-motors_9dea84dca87341f3b74c.pdf',
    general_risk:
      'https://reporting-service.api.yukkalab.com/content/general_risk_tesla-motors_245e597059614cf989f8.pdf',
    immediate_risk:
      'https://reporting-service.api.yukkalab.com/content/immediate_risk_tesla-motors_00be5901829a4778b2e5.pdf',
    esg_risk:
      'https://reporting-service.api.yukkalab.com/content/esg_risk_tesla-motors_8105bc5d58b8425396c9.pdf',
    esg: 'https://reporting-service.api.yukkalab.com/content/esg_tesla-motors_464ae01a768a44a8908a.pdf',
    eb_sim:
      'https://reporting-service.api.yukkalab.com/content/eb_sim_tesla-motors_f9b802f9b135420d8b43.pdf',
    eb_sim_risk:
      'https://reporting-service.api.yukkalab.com/content/eb_sim_risk_tesla-motors_dc8fe59f428c4bd68506.pdf',
    due_diligence_tabular:
      'https://reporting-service.api.yukkalab.com/content/due_diligence_tabular_tesla-motors_5dae601686154c87b781.pdf',
  },
  portfolio: {
    credit_watch:
      'https://reporting-service.api.yukkalab.com/content/credit_watch_auto_08bcacda733342239ba0.pdf',
    general_risk:
      'https://reporting-service.api.yukkalab.com/content/general_risk_auto_468bcb01566d4d54ae1a.pdf',
    immediate_risk:
      'https://reporting-service.api.yukkalab.com/content/immediate_risk_auto_160e954aaa3448ccb459.pdf',
    esg_risk:
      'https://reporting-service.api.yukkalab.com/content/esg_risk_auto_2a7e87cb20774e1396bc.pdf',
    esg: 'https://reporting-service.api.yukkalab.com/content/esg_auto_c64262705cca4feb8c1e.pdf',
    eb_sim:
      'https://reporting-service.api.yukkalab.com/content/eb_sim_auto_9033c7fabb154b539352.pdf',
    eb_sim_risk:
      'https://reporting-service.api.yukkalab.com/content/eb_sim_risk_auto_7a7ce424a33e460e9fbc.pdf',
    investment:
      'https://reporting-service.api.yukkalab.com/content/investment_auto_04eeea2f64754f6ea69f.pdf',
    due_diligence_tabular_portfolio:
      'https://reporting-service.api.yukkalab.com/content/due_diligence_tabular_portfolio_auto_ea961cb26b5d4f669c82.pdf',
    esgr_due_diligence_tabular_portfolio:
      'https://reporting-service.api.yukkalab.com/content/esgr_due_diligence_tabular_portfolio_auto_7f73a05796284f7b8fcd.pdf',
  },
};

export const REPORT_KIND_CONFIG: Record<
  ReportKind,
  { badge: string; bullets: string[]; defaultLabel: string }
> = {
  score: {
    badge: 'Score report',
    defaultLabel: 'Score Report',
    bullets: [
      'Score trends & top movers',
      'Top events & key risk indicators',
      'Industry benchmarks & peer comparisons',
    ],
  },
  due_diligence: {
    badge: 'Due Diligence',
    defaultLabel: 'Due Diligence',
    bullets: [
      'Top events ranked by severity',
      'Impact, status & supporting sources',
      'Score & sentiment over time',
    ],
  },
};

@Component({
  selector: 'app-dialog-report',
  templateUrl: './dialog-report.component.html',
  styleUrls: ['./dialog-report.component.scss'],
})
export class DialogReportComponent implements OnInit {
  reportStatus = '';
  selectedReportType!: ReportType;
  activeMode: ReportMode = 'get_now';
  frequency: Schedule = 'daily';
  recipientsInput = '';
  isScheduling = false;
  readonly frequencyOptions = FREQUENCY_OPTIONS;

  statusCheckLoading = false;
  statusCheckBlocked = false;
  private statusCheckRequestId = 0;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: ReportDialogData,
    private dialogRef: MatDialogRef<DialogReportComponent>,
    private userInfo: UserInfoService,
    private reportApi: ReportApiService,
    private matDialog: MatDialog,
    public snackBar: MatSnackBar,
    private reportFeedback: ReportFeedbackService,
  ) {}

  ngOnInit() {
    this.reportStatus = this.data.status ?? '';
    this.selectedReportType = this.data.reportType;
  }

  get reportKind(): ReportKind {
    return this.data.reportKind ?? 'score';
  }

  get isScoreReport(): boolean {
    return this.reportKind === 'score';
  }

  get isPortfolio(): boolean {
    return this.data.portfolioName != null;
  }

  get showDisclaimer(): boolean {
    return this.isPortfolio && !!this.data.messageLine;
  }

  get badgeLabel(): string {
    return REPORT_KIND_CONFIG[this.reportKind].badge;
  }

  get bullets(): string[] {
    return this.data.bullets ?? REPORT_KIND_CONFIG[this.reportKind].bullets;
  }

  get headerTitle(): string {
    const name = this.data.titleLine1?.trim() || '';
    const label =
      this.data.reportLabel?.trim() ||
      REPORT_KIND_CONFIG[this.reportKind].defaultLabel;
    return name ? `${name} · ${label}` : label;
  }

  setMode(tab: ReportMode): void {
    this.activeMode = tab;
  }

  onClose(): void {
    this.dialogRef.close();
  }

  onScoreTypeChange(value: ReportType): void {
    this.selectedReportType = value;
    // Reverted to the initial type — its status was already checked upstream.
    if (value === this.data.reportType) {
      this.statusCheckLoading = false;
      this.statusCheckBlocked = !isStatusAvailable(this.data.status ?? null);
      return;
    }
    this.runSelectedTypeStatusCheck(value);
  }

  private runSelectedTypeStatusCheck(value: ReportType): void {
    const reqId = ++this.statusCheckRequestId;
    this.statusCheckLoading = true;
    this.statusCheckBlocked = false;

    const date = moment(new Date()).format('YYYY-MM-DD');
    const status$ = this.isPortfolio
      ? this.reportApi.getReportStatusPortfolio(
          this.data.portfolioName!,
          this.data.portfolioContent ?? [],
          value,
          date,
        )
      : this.reportApi.getReportStatus(this.data.entityId!, value, date);

    status$.subscribe({
      next: (res: { status?: string } | null) => {
        if (reqId !== this.statusCheckRequestId) return;
        this.statusCheckLoading = false;
        this.statusCheckBlocked = !isStatusAvailable(res?.status ?? null);
      },
      error: () => {
        if (reqId !== this.statusCheckRequestId) return;
        this.statusCheckLoading = false;
        this.statusCheckBlocked = false;
      },
    });
  }

  get downloadDisabled(): boolean {
    return (
      this.reportStatus === 'download_quota_exceeded' ||
      this.statusCheckLoading ||
      this.statusCheckBlocked
    );
  }

  get downloadTooltip(): string {
    if (this.reportStatus === 'download_quota_exceeded') {
      return 'Monthly Report Download Limit Exceeded';
    }
    if (this.statusCheckBlocked) {
      return 'Selected report type is not available for this entity.';
    }
    return '';
  }

  get previewUrl(): string | null {
    const scope = this.isPortfolio ? 'portfolio' : 'entity';
    return PREVIEW_URLS[scope][this.selectedReportType] ?? null;
  }

  onExample(): void {
    const url = this.previewUrl;
    if (url) {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  }

  setFrequency(value: Schedule): void {
    this.frequency = value;
  }

  get currentFrequency(): FrequencyOption {
    return (
      this.frequencyOptions.find((f) => f.value === this.frequency) ??
      this.frequencyOptions[0]
    );
  }

  get parsedRecipients(): string[] {
    return this.recipientsInput
      .split(',')
      .map((e) => e.trim())
      .filter((e) => e.length > 0);
  }

  get canSchedule(): boolean {
    if (this.isScheduling) return false;
    if (this.data.searchQuery) return !!this.data.queryName;
    return this.isPortfolio ? !!this.data.portfolioUid : !!this.data.entityId;
  }

  private buildSubscriptionPayload(): SubscriptionPayload | null {
    if (this.data.searchQuery) {
      if (!this.data.queryName) return null;
      const sq = this.data.searchQuery as unknown as Record<string, unknown>;
      return {
        kind: 'query',
        queryName: this.data.queryName,
        searchQuery: sq,
      };
    }
    if (this.isPortfolio) {
      if (!this.data.portfolioUid || !this.data.portfolioName) return null;
      return {
        kind: 'portfolio',
        portfolioId: this.data.portfolioUid,
        portfolioName: this.data.portfolioName,
        companyIds: this.data.portfolioContent ?? [],
      };
    }
    if (!this.data.entityId) return null;
    return {
      kind: 'company',
      companyId: this.data.entityId,
    };
  }

  onConfirmSchedule(): void {
    if (!this.canSchedule) return;
    const email = this.userInfo.email;
    if (!email) return;
    const payload = this.buildSubscriptionPayload();
    if (!payload) return;

    const reportType = this.selectedReportType;

    this.isScheduling = true;
    this.reportApi
      .createSubscription({
        email,
        schedule: this.frequency,
        schedulerType: this.frequency,
        reportType,
        reportFormat: 'pdf',
        recipients: this.parsedRecipients,
        payload,
      })
      .subscribe({
        next: () => {
          this.isScheduling = false;
          this.dialogRef.close();
          const ref = this.snackBar.open(
            'Report scheduled successfully.',
            'Manage subscriptions',
            { duration: 8000, panelClass: ['view-query-snackbar'] },
          );
          ref.onAction().subscribe(() => this.openManageSubscriptions());
        },
        error: () => {
          this.isScheduling = false;
          this.snackBar.open(
            'Something went wrong, the report schedule could not be saved.',
            'OK',
            { duration: 5000 },
          );
        },
      });
  }

  private openManageSubscriptions(): void {
    this.matDialog.open(AlertsSetupComponent, {
      autoFocus: false,
      data: { initialTab: 'reports' },
    });
  }

  onClick() {
    const date = moment(new Date()).format('YYYY-MM-DD');
    const type = this.selectedReportType;

    let send$: Observable<unknown>;
    if (this.data.searchQuery) {
      send$ = this.reportApi.sendQueryReport(
        this.data.searchQuery,
        this.data.queryName!,
        type,
        date,
        this.parsedRecipients,
      );
    } else if (this.data.portfolioName != null) {
      send$ = this.reportApi.sendReportPortfolio(
        this.data.portfolioName,
        this.data.portfolioContent!,
        type,
        date,
        this.parsedRecipients,
      );
    } else {
      send$ = this.reportApi.sendReport(
        this.data.entityId!,
        type,
        date,
        this.parsedRecipients,
      );
    }

    send$.subscribe({
      next: () => {
        this.dialogRef.close();
        this.reportFeedback.reportSent();
      },
      error: () => {
        this.dialogRef.close();
        this.reportFeedback.reportError();
      },
    });
  }
}
